import { List, Map, Set } from "immutable";

import { type ViewColumnDef } from "@moment/api-collab/api-types";

import { RunnableQuery, topologicallySortQueries } from "../RunnableQuery";
import { type Tuple } from "../Tuple";
import { deleteChange } from "../Tuples";
import { insert } from "../Tuples/Change";
import { ZSet } from "../ZSet";
import { type JsonPathLiteral } from "../jsonTable/JsonPathLiteral";
import { jsonPathToMergePatchPath } from "../jsonTable/jsonPathToMergePatchPath";
import { pluck } from "../jsonTable/pluck";
import { type TopLevelQuerySpec, querySpecId } from "../operatorsV2/QuerySpec";
import {
	type Action,
	type ColumnAppendAction,
	type ColumnDeleteAction,
	type ColumnRenameAction,
} from "./Action";
import {
	type ActionIntent,
	type ColumnAppendActionIntent,
	type ColumnDeleteActionIntent,
	type ColumnRenameActionIntent,
	type MutationActionIntent,
	isColumnAppendActionIntent,
	isColumnDeleteActionIntent,
	isColumnRenameActionIntent,
	isMutationActionIntent,
	reconcileQueryEvaluatorState,
} from "./ActionIntent";
import { type QueryEvaluatorState, emptyQueryEvaluatorState } from "./QueryManagerState";
import { type TableMutation } from "./TableMutation";

export * from "./Action";
export * from "./QueryManagerState";

export class QueryEvaluator {
	private _committedState: QueryEvaluatorState;
	private _actionIntents: List<ActionIntent> = List();
	private _topoSortedQueries: RunnableQuery[];

	constructor(
		tables: { [key: string]: unknown },
		private _maintainEphemeralLog = true
	) {
		const initialState = emptyQueryEvaluatorState();
		for (const tableId of Object.keys(tables)) {
			initialState.tables = initialState.tables.set(tableId, ZSet.empty());
		}
		this._committedState = initialState;
		this._topoSortedQueries = topologicallySortQueries(initialState.querySpecs);
	}

	get latestCommittedState(): QueryEvaluatorState {
		return this._committedState;
	}

	get latestState(): QueryEvaluatorState {
		return this._actionIntents.last()?.proposedState ?? this._committedState;
	}

	get pendingActions(): List<ActionIntent> {
		return this._actionIntents;
	}

	queryIntent(query: TopLevelQuerySpec): ActionIntent {
		const state = this.latestState;

		let newQuerySpecs = state.querySpecs;
		let newQueryResults = state.queryResults;
		let newQueryStates = state.queryStates;
		const rootId = querySpecId(query.spec).contentId;
		for (const querySpec of [...query.subquerySpecs, query.spec]) {
			const { contentId: queryId } = querySpecId(querySpec);
			const newQuery =
				rootId === queryId
					? RunnableQuery.fromSpec(querySpec, query.finalizer)
					: RunnableQuery.fromSpec(querySpec);
			newQuerySpecs = newQuerySpecs.set(queryId, newQuery);
			const { result, newStates } = newQuery.update(
				this.latestState.tables,
				newQueryResults,
				newQueryStates.get(queryId)
			);
			newQueryResults = newQueryResults.set(queryId, result);
			newQueryStates = newQueryStates.set(queryId, newStates);
		}
		const newTopoSortedQueries = topologicallySortQueries(newQuerySpecs);

		// Create new state with updated specs and sorted queries
		const action: Action = {
			type: "queryRegistration",
			intentId: crypto.randomUUID(),
			desiredStartEpoch: state.currentEpoch,
			querySpec: query,
		};
		const newState: QueryEvaluatorState = {
			currentEpoch: state.currentEpoch + 1,
			tables: state.tables,
			querySpecs: newQuerySpecs,
			queryResults: newQueryResults,
			queryStates: newQueryStates,
			actionLog: state.actionLog.push(action),
		};
		const actionIntent: ActionIntent = { action, proposedState: newState };

		if (this._maintainEphemeralLog) {
			this._topoSortedQueries = newTopoSortedQueries;
			this._actionIntents = this._actionIntents.push(actionIntent);
		}

		return actionIntent;
	}

	mutationIntent(
		tableId: string,
		desiredApplyEpoch: number,
		mutation: TableMutation<unknown>
	): MutationActionIntent {
		const currentEpoch = this.lastActionEpoch();

		if (desiredApplyEpoch !== currentEpoch) {
			throw new Error("Epoch mismatch");
		}

		// Apply operations to the table
		const table = this._committedState.tables.get(tableId)?.clone();
		if (!table) {
			throw new Error(`Table ${tableId} not found`);
		}

		if (mutation.type === "INSERT" && table?.hasId(mutation.record.id)) {
			throw new Error(`Record ${mutation.record.id} already exists`);
		} else if (mutation.type === "UPDATE" && !table?.hasId(mutation.recordId)) {
			throw new Error(`Record ${mutation.recordId} does not exist`);
		} else if (mutation.type === "DELETE" && !table?.hasId(mutation.recordId)) {
			throw new Error(`Record ${mutation.recordId} does not exist`);
		}

		const delta = table.deltaFromMutations(mutation);
		const currTables = this._committedState.tables.set(tableId, table.merge(delta));
		let currQueryDelta = Map<string, ZSet<unknown>>();
		let currQueryStates = this.latestState.queryStates;

		let updatedQueryIds = Set<string>();
		let changesByQueryId = Map<string, List<TableMutation<unknown>>>();
		const unchangedTables = currTables
			.keySeq()
			.map<[string, ZSet<unknown>]>((key) => [key, ZSet.empty<unknown>()]);
		for (const query of this._topoSortedQueries) {
			const { result, newStates } = query.update(
				Map([...unchangedTables, [tableId, delta]]),
				currQueryDelta,
				currQueryStates.get(query.contentId)
			);
			currQueryDelta = currQueryDelta.set(query.contentId, result);
			currQueryStates = currQueryStates.set(query.contentId, newStates);
			if (result.size > 0) {
				updatedQueryIds = updatedQueryIds.add(query.contentId);
			}

			let changes = List<TableMutation<unknown>>();
			for (const { tuple, weight } of result) {
				if (weight > 0) {
					changes = changes.push(insert(tuple));
				} else if (weight < 0) {
					changes = changes.push(deleteChange(tuple.id));
				}
			}
			changesByQueryId = changesByQueryId.set(query.contentId, changes);
		}

		let queryResults = this.latestState.queryResults;

		for (const [queryId, delta] of currQueryDelta) {
			const currentResults = queryResults.get(queryId) ?? ZSet.empty();
			queryResults = queryResults.set(queryId, currentResults.merge(delta));
		}

		// Update the state
		const mutationIntentId = crypto.randomUUID();
		const action: Action = {
			type: "mutation",
			intentId: mutationIntentId,
			table: tableId,
			desiredApplyEpoch,
			mutation,
		};
		const proposedStateUpdate: ActionIntent = {
			action,
			proposedState: {
				currentEpoch: this._committedState.currentEpoch + 1,
				tables: currTables,
				querySpecs: this._committedState.querySpecs,
				queryResults,
				queryStates: currQueryStates,
				actionLog: this._committedState.actionLog.push(action),
			},
			changesByQueryId,
		};

		if (this._maintainEphemeralLog) {
			this._actionIntents = this._actionIntents.push(proposedStateUpdate);
		}
		return proposedStateUpdate;
	}

	columnAppendIntent(tableId: string, column: ViewColumnDef): ColumnAppendActionIntent {
		//
		// Get table.
		//

		const table = this._committedState.tables.get(tableId);
		if (!table) {
			throw new Error(`Table ${tableId} not found`);
		}

		//
		// Remove and re-add every row with a `NULL` value for the new column.
		//

		let delta = ZSet.empty<unknown>();
		for (const row of table) {
			// Remove current version of the row.
			delta = table.add({ ...row, weight: -1 });

			// Add new version of the row.
			const newTuple = row.tuple.addOrReplace(column.accessorKey, null);
			delta = table.add({ ...row, weight: 1, tuple: newTuple });
		}

		//
		// Update the current tables.
		//

		const currTables = this._committedState.tables.set(tableId, table.merge(delta));

		//
		// Update the query results.
		//

		let currQueryDelta = Map<string, ZSet<unknown>>();
		let currQueryStates = this.latestState.queryStates;

		let updatedQueryIds = Set<string>();
		let changesByQueryId = Map<string, List<TableMutation<unknown>>>();
		const unchangedTables = currTables
			.keySeq()
			.map<[string, ZSet<unknown>]>((key) => [key, ZSet.empty<unknown>()]);
		for (const query of this._topoSortedQueries) {
			const { result, newStates } = query.update(
				Map([...unchangedTables, [tableId, delta]]),
				currQueryDelta,
				currQueryStates.get(query.contentId)
			);
			currQueryDelta = currQueryDelta.set(query.contentId, result);
			currQueryStates = currQueryStates.set(query.contentId, newStates);
			if (result.size > 0) {
				updatedQueryIds = updatedQueryIds.add(query.contentId);
			}

			let changes = List<TableMutation<unknown>>();
			for (const { tuple, weight } of result) {
				if (weight > 0) {
					changes = changes.push(insert(tuple));
				} else if (weight < 0) {
					changes = changes.push(deleteChange(tuple.id));
				}
			}
			changesByQueryId = changesByQueryId.set(query.contentId, changes);
		}

		let queryResults = this.latestState.queryResults;

		for (const [queryId, delta] of currQueryDelta) {
			const currentResults = queryResults.get(queryId) ?? ZSet.empty();
			queryResults = queryResults.set(queryId, currentResults.merge(delta));
		}

		//
		// Construct the return value.
		//

		const action: ColumnAppendAction = {
			type: "columnAppend",
			intentId: crypto.randomUUID(),
			table: tableId,
			desiredApplyEpoch: this._committedState.currentEpoch,
			column,
		};

		const proposedState: QueryEvaluatorState = {
			currentEpoch: this.lastActionEpoch(),
			tables: currTables,
			querySpecs: this._committedState.querySpecs,
			queryResults,
			queryStates: currQueryStates,
			actionLog: this._committedState.actionLog.push(action),
		};

		return { action, proposedState, changesByQueryId };
	}

	columnDeleteIntent(tableId: string, column: ViewColumnDef): ColumnDeleteActionIntent {
		//
		// Get table.
		//

		const table = this._committedState.tables.get(tableId);
		if (!table) {
			throw new Error(`Table ${tableId} not found`);
		}

		//
		// Remove and re-add every row without the specified column.
		//

		let delta = ZSet.empty<unknown>();
		for (const row of table) {
			let newTuple: Tuple<unknown>;
			try {
				const patchPath = jsonPathToMergePatchPath(column.accessorKey);
				newTuple = row.tuple.applyPatch([{ op: "remove", path: patchPath }]);
			} catch (error) {
				// Skip the record if there is no column to remove.
				continue;
			}

			// Remove current version of the row.
			delta = table.add({ ...row, weight: -1 });

			// Add new version of the row without the specified column.
			delta = table.add({ ...row, weight: 1, tuple: newTuple });
		}

		//
		// Update the current tables.
		//

		const currTables = this._committedState.tables.set(tableId, table.merge(delta));

		//
		// Update the query results.
		//

		let currQueryDelta = Map<string, ZSet<unknown>>();
		let currQueryStates = this.latestState.queryStates;

		let updatedQueryIds = Set<string>();
		let changesByQueryId = Map<string, List<TableMutation<unknown>>>();
		const unchangedTables = currTables
			.keySeq()
			.map<[string, ZSet<unknown>]>((key) => [key, ZSet.empty<unknown>()]);
		for (const query of this._topoSortedQueries) {
			const { result, newStates } = query.update(
				Map([...unchangedTables, [tableId, delta]]),
				currQueryDelta,
				currQueryStates.get(query.contentId)
			);
			currQueryDelta = currQueryDelta.set(query.contentId, result);
			currQueryStates = currQueryStates.set(query.contentId, newStates);
			if (result.size > 0) {
				updatedQueryIds = updatedQueryIds.add(query.contentId);
			}

			let changes = List<TableMutation<unknown>>();
			for (const { tuple, weight } of result) {
				if (weight > 0) {
					changes = changes.push(insert(tuple));
				} else if (weight < 0) {
					changes = changes.push(deleteChange(tuple.id));
				}
			}
			changesByQueryId = changesByQueryId.set(query.contentId, changes);
		}

		let queryResults = this.latestState.queryResults;

		for (const [queryId, delta] of currQueryDelta) {
			const currentResults = queryResults.get(queryId) ?? ZSet.empty();
			queryResults = queryResults.set(queryId, currentResults.merge(delta));
		}

		//
		// Construct the return value.
		//

		const action: ColumnDeleteAction = {
			type: "columnDelete",
			intentId: crypto.randomUUID(),
			table: tableId,
			desiredApplyEpoch: this._committedState.currentEpoch,
			column,
		};

		const proposedState: QueryEvaluatorState = {
			currentEpoch: this.lastActionEpoch(),
			tables: currTables,
			querySpecs: this._committedState.querySpecs,
			queryResults,
			queryStates: currQueryStates,
			actionLog: this._committedState.actionLog.push(action),
		};

		return { action, proposedState, changesByQueryId };
	}

	columnRenameIntent(
		tableId: string,
		oldColumnAccessorKey: JsonPathLiteral,
		newHeader: string,
		newAccessorKey: JsonPathLiteral
	): ColumnRenameActionIntent {
		//
		// Get table.
		//

		const table = this._committedState.tables.get(tableId);
		if (!table) {
			throw new Error(`Table ${tableId} not found`);
		}

		//
		// Remove and re-add every row with the renamed column.
		//

		let delta = ZSet.empty<unknown>();
		for (const row of table) {
			let newTuple: Tuple<unknown>;
			try {
				// First remove the old column
				const oldPatchPath = jsonPathToMergePatchPath(oldColumnAccessorKey);
				const value = row.tuple.getValue();
				const oldValue = pluck(value, oldColumnAccessorKey);

				// Create new tuple with the column renamed
				newTuple = row.tuple
					.applyPatch([{ op: "remove", path: oldPatchPath }])
					.addOrReplace(newAccessorKey, oldValue);
			} catch (error) {
				// Skip the record if there is no column to rename
				continue;
			}

			// Remove current version of the row
			delta = delta.add({ ...row, weight: -1 });

			// Add new version of the row with the renamed column
			delta = delta.add({ ...row, weight: 1, tuple: newTuple });
		}

		//
		// Update the current tables.
		//

		const currTables = this._committedState.tables.set(tableId, table.merge(delta));

		//
		// Update the query results.
		//

		let currQueryDelta = Map<string, ZSet<unknown>>();
		let currQueryStates = this.latestState.queryStates;

		let updatedQueryIds = Set<string>();
		let changesByQueryId = Map<string, List<TableMutation<unknown>>>();
		const unchangedTables = currTables
			.keySeq()
			.map<[string, ZSet<unknown>]>((key) => [key, ZSet.empty<unknown>()]);
		for (const query of this._topoSortedQueries) {
			const { result, newStates } = query.update(
				Map([...unchangedTables, [tableId, delta]]),
				currQueryDelta,
				currQueryStates.get(query.contentId)
			);
			currQueryDelta = currQueryDelta.set(query.contentId, result);
			currQueryStates = currQueryStates.set(query.contentId, newStates);
			if (result.size > 0) {
				updatedQueryIds = updatedQueryIds.add(query.contentId);
			}

			let changes = List<TableMutation<unknown>>();
			for (const { tuple, weight } of result) {
				if (weight > 0) {
					changes = changes.push(insert(tuple));
				} else if (weight < 0) {
					changes = changes.push(deleteChange(tuple.id));
				}
			}
			changesByQueryId = changesByQueryId.set(query.contentId, changes);
		}

		let queryResults = this.latestState.queryResults;

		for (const [queryId, delta] of currQueryDelta) {
			const currentResults = queryResults.get(queryId) ?? ZSet.empty();
			queryResults = queryResults.set(queryId, currentResults.merge(delta));
		}

		//
		// Construct the return value.
		//

		const action: ColumnRenameAction = {
			type: "columnRename",
			intentId: crypto.randomUUID(),
			table: tableId,
			desiredApplyEpoch: this._committedState.currentEpoch,
			oldColumnAccessorKey,
			newHeader,
			newAccessorKey,
		};

		const proposedState: QueryEvaluatorState = {
			currentEpoch: this.lastActionEpoch(),
			tables: currTables,
			querySpecs: this._committedState.querySpecs,
			queryResults,
			queryStates: currQueryStates,
			actionLog: this._committedState.actionLog.push(action),
		};

		return { action, proposedState, changesByQueryId };
	}

	ensureReconciled(sourceOfTruthState: QueryEvaluatorState): void {
		const reconciliationResult = reconcileQueryEvaluatorState(
			sourceOfTruthState,
			this._actionIntents
		);
		this._committedState = reconciliationResult.newCommittedState;
		this._actionIntents = reconciliationResult.remainingPendingActions;
		this._topoSortedQueries = topologicallySortQueries(this._committedState.querySpecs);
	}

	private lastActionEpoch(): number {
		const lastAction = this._actionIntents.last();
		const lastActionEpoch =
			isMutationActionIntent(lastAction) ||
			isColumnAppendActionIntent(lastAction) ||
			isColumnDeleteActionIntent(lastAction) ||
			isColumnRenameActionIntent(lastAction)
				? lastAction.action.desiredApplyEpoch
				: lastAction?.action.desiredStartEpoch;
		return lastActionEpoch ?? this._committedState.currentEpoch;
	}
}
